import React, { lazy } from 'react';
import { useIntl } from 'react-intl';

import Page from '@authFlow/App/Routing/Page';
import { isTabPlansTemplate } from '@authFlow/utils/signupHelpers';

import type { CustomAuthT } from '@authFlow/types/types';

// signup flow

const SignUpPage = lazy(
  () =>
    import(
      /* webpackChunkName: "signup" */
      '@authFlow/pages/Signup/SignUpPage/SignUpPage'
    ),
);

const ConfirmEmailFormWithOTP = lazy(
  () =>
    /* webpackChunkName: "confirmemailwithotp" */
    import('@authFlow/pages/ConfirmEmailForm'),
);

const SubscriptionConsentPage = lazy(
  () =>
    /* webpackChunkName: "subscriptionconsent" */
    import('@authFlow/pages/SubscriptionConsentPage'),
);

const Survey = lazy(
  () =>
    /* webpackChunkName: "survey" */
    import('@authFlow/pages/Survey'),
);

const RequestEnterprise = lazy(
  () =>
    /* webpackChunkName: "requestenterprisepage" */
    import('@authFlow/pages/Enterprise'),
);

const BookEnterprise = lazy(
  () =>
    /* webpackChunkName: "bookenterprisepage" */
    import('@authFlow/pages/BookEnterprise'),
);

const DomainPage = lazy(
  () =>
    /* webpackChunkName: "domainpage" */
    import('@authFlow/pages/Domain'),
);

const InsightsPage = lazy(
  () =>
    /* webpackChunkName: "insightspage" */
    import('@authFlow/pages/Insights'),
);

const GetInsights = lazy(
  () =>
    /* webpackChunkName: "getinsights" */
    import('@authFlow/pages/GetInsights'),
);

const PersonalisedPlans = lazy(
  () =>
    import(
      /* webpackChunkName: "personalisedplans" */
      '@authFlow/pages/PersonalisedPlans'
    ),
);

const Plans = lazy(
  () =>
    import(
      /* webpackChunkName: "plans" */
      '@authFlow/pages/Plans'
    ),
);

const GetSubscriptionPayment = lazy(
  () =>
    /* webpackChunkName: "getsubscriptionpayment" */
    import('@authFlow/pages/GetSubscription/GetSubscriptionPayment'),
);

const Subscribe = lazy(
  () =>
    import(
      /* webpackChunkName: "subscribe" */
      '@authFlow/pages/Subscribe'
    ),
);

const ProductSuccess = lazy(
  () =>
    import(
      /* webpackChunkName: "productsuccess" */
      '@authFlow/components/ProductSuccess/ProductSuccess'
    ),
);

const ProductFail = lazy(
  () =>
    import(
      /* webpackChunkName: "productfail" */
      '@authFlow/components/ProductFail/ProductFail'
    ),
);

const InviteUsers = lazy(
  () =>
    /* webpackChunkName: "inviteusers" */
    import('@authFlow/pages/InviteUsers'),
);

const ProvideInfoPage = lazy(
  () =>
    /* webpackChunkName: "provideinfopage" */
    import('@authFlow/pages/ProvideInfoPage'),
);

// login

const LoginPage = lazy(
  () =>
    import(
      /* webpackChunkName: "login" */
      '@authFlow/pages/Login'
    ),
);

const VerificationPage = lazy(
  () =>
    /* webpackChunkName: "emailverification" */
    import('@authFlow/pages/EmailVerification'),
);

const RestoreView = lazy(
  () =>
    import(
      /* webpackChunkName: "restore" */
      '@authFlow/pages/RestoreView/RestoreView'
    ),
);

const ChangePassword = lazy(
  () =>
    import(
      /* webpackChunkName: "change" */
      '@authFlow/pages/ChangePassword'
    ),
);

const ConnectGooglePage = lazy(
  () =>
    import(
      /* webpackChunkName: "connect" */
      '@authFlow/pages/ConnectGoogleAccount'
    ),
);

const SoftBlockPage = lazy(
  () =>
    /* webpackChunkName: "softblockpage" */
    import('@authFlow/pages/BlockPage/SoftBlockPage'),
);

const HardBlockPage = lazy(
  () =>
    /* webpackChunkName: "hardblockpage" */
    import('@authFlow/pages/BlockPage/HardBlockPage'),
);

const DisablePage = lazy(
  () =>
    /* webpackChunkName: "blockpage" */
    import('@authFlow/pages/BlockPage/DisablePage'),
);

const SAMLPage = lazy(
  () =>
    /* webpackChunkName: "samllogin" */
    import('@authFlow/pages/SAML'),
);

const ThirdPartyAuth = lazy(
  () =>
    /* webpackChunkName: "thirdpartyauth" */
    import('@authFlow/components/ThirdPartyAuth/ThirdPartyAuth'),
);

const EnterEmailPage = lazy(
  () =>
    /* webpackChunkName: "enteremailpage" */
    import('@authFlow/pages/EnterEmailPage'),
);

// promo

const PromoPage = lazy(
  () =>
    import(
      /* webpackChunkName: "promo" */
      '@authFlow/pages/Promo/components/PromoPage/PromoPage'
    ),
);

// experiments

const DesktopRecommended = lazy(
  () =>
    /* webpackChunkName: "desktoprecommended" */
    import('@authFlow/pages/DesktopRecommended'),
);

const ToolkitSelection = lazy(
  () =>
    import(
      /* webpackChunkName: "toolkitselection" */
      '@authFlow/pages/ToolkitSelection'
    ),
);

const MarketingSource = lazy(
  () =>
    /* webpackChunkName: "marketingsource" */
    import('@authFlow/pages/MarketingSource'),
);

type Props = {
  auth: CustomAuthT;
  isChina: boolean;
  textCaptchaUrl: string;
  countryCode: string;
  headerTitleLogin?: string;
  signupOfferTitle?: string;
};

const Pages = (props: Props) => {
  const intl = useIntl();

  return (
    <>
      <Page
        path="/signup/"
        title={intl.formatMessage({
          defaultMessage: 'Sign up',
        })}
      >
        <SignUpPage
          auth={props.auth}
          isChina={props.isChina}
          textCaptchaUrl={props.textCaptchaUrl}
          signupOfferTitle={props.signupOfferTitle}
        />
      </Page>

      <Page
        path="/signup/confirm/:auth_from?"
        title={intl.formatMessage({
          defaultMessage: 'Confirm Your Email',
        })}
      >
        <ConfirmEmailFormWithOTP auth={props.auth} />
      </Page>

      <Page
        path="/signup/subscription-consent/"
        title={intl.formatMessage({
          defaultMessage: 'Sign up',
        })}
      >
        <SubscriptionConsentPage />
      </Page>

      <Page
        path="/signup/customize-flow/"
        title={intl.formatMessage({
          defaultMessage: 'Let’s customize your experience',
        })}
      >
        <Survey />
      </Page>

      <Page
        path="/signup/project-info/"
        title={intl.formatMessage({
          defaultMessage: 'Enter your website',
        })}
      >
        <DomainPage />
      </Page>

      <Page
        path="/signup/enterprise/"
        title={intl.formatMessage({
          defaultMessage: 'Best solution for enterprise',
        })}
      >
        <RequestEnterprise />
      </Page>

      <Page
        path="/signup/enterprise-demo/"
        title={intl.formatMessage({
          defaultMessage: 'Book Enterprise Demo',
        })}
      >
        <BookEnterprise />
      </Page>

      <Page
        path="/signup/insights/"
        title={intl.formatMessage({
          defaultMessage: 'AI Visibility Insights',
        })}
      >
        <InsightsPage />
      </Page>

      <Page
        path="/signup/get-insights/"
        title={intl.formatMessage({
          defaultMessage: 'Uncover AI insights',
        })}
      >
        <GetInsights />
      </Page>

      <Page
        path="/signup/get-free-trial/"
        title={intl.formatMessage({
          defaultMessage: 'Choose your trial plan',
        })}
      >
        {isTabPlansTemplate() ? <PersonalisedPlans /> : <Plans />}
      </Page>

      <Page
        path="/signup/get-subscription/"
        title={intl.formatMessage({
          defaultMessage: 'Subscribe',
        })}
      >
        <Plans />
      </Page>

      <Page
        path="/subscribe/"
        title={intl.formatMessage({
          defaultMessage: 'Subscribe',
        })}
      >
        <Subscribe fromChooseTrial={false} />
      </Page>

      <Page
        path="/signup/get-free-trial/([\w-]+)/"
        title={intl.formatMessage({
          defaultMessage: 'Choose your trial plan',
        })}
      >
        <Subscribe fromChooseTrial={true} />
      </Page>

      <Page
        path="/product/success/"
        title={intl.formatMessage({
          defaultMessage: 'Payment successful',
        })}
      >
        <ProductSuccess />
      </Page>

      <Page
        path="/product/fail/"
        title={intl.formatMessage({
          defaultMessage: 'Payment failed',
        })}
      >
        <ProductFail />
      </Page>

      <Page
        path="/signup/get-subscription/payment/"
        title={intl.formatMessage({
          defaultMessage: 'Subscribe',
        })}
      >
        <GetSubscriptionPayment />
      </Page>

      <Page
        path="/signup/invite/"
        title={intl.formatMessage({
          defaultMessage: 'Invite your colleagues',
          description: 'Page title in browser tab',
        })}
      >
        <InviteUsers />
      </Page>

      <Page
        path="/signup/info/"
        title={intl.formatMessage({
          defaultMessage: 'Sign up',
        })}
      >
        <ProvideInfoPage />
      </Page>

      <Page
        path="/login/"
        title={intl.formatMessage({
          defaultMessage: 'Log in',
          description: 'Page title',
        })}
      >
        <LoginPage
          auth={props.auth}
          isChina={props.isChina}
          headerTitle={props.headerTitleLogin}
        />
      </Page>

      <Page
        path="/login/saml/"
        title={intl.formatMessage({
          defaultMessage: 'Log in with SAML',
        })}
      >
        <SAMLPage auth={props.auth} />
      </Page>

      <Page
        path="/login/saml-error/"
        title={intl.formatMessage({
          defaultMessage: 'Log in with SAML',
        })}
      >
        <SAMLPage auth={props.auth} type={'error'} />
      </Page>

      <Page
        path="/login/saml-success/"
        title={intl.formatMessage({
          defaultMessage: 'Log in with SAML',
        })}
      >
        <SAMLPage auth={props.auth} type={'success'} />
      </Page>

      <Page
        path="/login/restore/"
        title={intl.formatMessage({
          defaultMessage: 'Reset your password',
        })}
      >
        <RestoreView auth={props.auth} />
      </Page>

      <Page
        path="/login/change/"
        title={intl.formatMessage({
          defaultMessage: 'Change password',
        })}
      >
        <ChangePassword auth={props.auth} />
      </Page>

      <Page
        path="/login/confirm/"
        title={intl.formatMessage({
          defaultMessage: 'Account exist',
        })}
      >
        <ConnectGooglePage auth={props.auth} />
      </Page>

      <Page
        path="/login/2fa/"
        title={intl.formatMessage({
          defaultMessage: 'Enter your code in the field below',
        })}
      >
        <VerificationPage auth={props.auth} />
      </Page>

      <Page
        path="/login/disable_soft/"
        title={intl.formatMessage({
          defaultMessage: 'Account disabled',
        })}
      >
        <SoftBlockPage />
      </Page>

      <Page
        path="/login/disable_hard/"
        title={intl.formatMessage({
          defaultMessage: 'Account disabled',
        })}
      >
        <HardBlockPage />
      </Page>

      <Page
        path="/login/disable/"
        title={intl.formatMessage({
          defaultMessage: 'Account disabled',
        })}
      >
        <DisablePage />
      </Page>

      <Page
        path="/signup/facebook/"
        title={intl.formatMessage({
          defaultMessage: 'Create your account',
        })}
      >
        <ThirdPartyAuth auth={props.auth} social={'facebook'} />
      </Page>

      <Page
        path="/signup/linkedin/"
        title={intl.formatMessage({
          defaultMessage: 'Create your account',
        })}
      >
        <ThirdPartyAuth auth={props.auth} social={'linkedin'} />
      </Page>

      <Page
        path="/signup/email/"
        title={intl.formatMessage({
          defaultMessage: 'Enter your email',
        })}
      >
        <EnterEmailPage auth={props.auth} />
      </Page>

      <Page
        path="/signup/promo/"
        title={intl.formatMessage({
          defaultMessage: 'Register Your Semrush Promo Code',
        })}
      >
        <PromoPage />
      </Page>
      <Page
        path="/signup/desktop-recommended/"
        title={intl.formatMessage({
          defaultMessage: 'Desktop Recommended',
        })}
      >
        <DesktopRecommended />
      </Page>

      <Page
        path="/signup/toolkit-selection/"
        title={'Select a toolkit for a head start'}
      >
        <ToolkitSelection />
      </Page>

      <Page
        path="/signup/marketing-source/"
        title={intl.formatMessage({
          defaultMessage: 'How did you hear about us',
        })}
      >
        <MarketingSource />
      </Page>
    </>
  );
};

export default Pages;
